import React, { useState, useMemo, useRef } from 'react';
import { Box, Button, FormField, Icon, Modal, Textarea, Spinner } from '@amzn/awsui-components-core';
import { TimeSlot, MeetingComment, TimeSlotVote } from '../../interfaces/interface';
import { formatTimeSlotDisplay, getUserTimezone, formatTimeSlotLocalLabel } from 'src/utilities/DateTimeUtilities';
import { useTimeSlotComments } from '../../hooks/useTimeSlotComments';
import { CommentItem } from './CommentItem';
import { DateTime } from 'luxon';
import './TimeSlotCard.scss';

interface TimeSlotCardProps {
  slot: TimeSlot;
  currentUserAlias: string;
  onVote: (slotId: string, vote: 'up' | 'down') => void;
  meetingId: string | null;
  creatorAlias: string | null;
  meetingSubject?: string;
  isLocked?: boolean;
}

export const TimeSlotCard: React.FC<TimeSlotCardProps> = ({
  slot,
  currentUserAlias,
  onVote,
  meetingId,
  creatorAlias,
  meetingSubject,
  isLocked = false
}) => {
  const upVoters = slot.votes.filter((v) => v.vote === 'up');
  const downVoters = slot.votes.filter((v) => v.vote === 'down');
  const currentUserVote = slot.votes.find((v) => v.loginName.toLowerCase() === currentUserAlias.toLowerCase());

  const displayText = formatTimeSlotDisplay(slot.date, slot.startTime, slot.endTime, slot.timezone);

  const userTimezone = useMemo(() => getUserTimezone(), []);
  const tzDisplay = useMemo(() => {
    const effectiveTz = slot.timezone || userTimezone;
    const displayNoTz = displayText.replace(/\s+([A-Z]{2,5}|[+-]\d{2}:\d{2})$/, '').trim();
    const slotDt = DateTime.fromISO(`${slot.date}T${slot.startTime}`, { zone: effectiveTz });
    const slotAbbr = slotDt.toFormat('ZZZZ');

    const userDt = slotDt.setZone(userTimezone);
    if (slotDt.offset === userDt.offset) {
      return { slotAbbr, displayNoTz, localInfo: null };
    }

    const userAbbr = userDt.toFormat('ZZZZ');
    const localStart = DateTime.fromISO(`${slot.date}T${slot.startTime}`, { zone: effectiveTz }).setZone(userTimezone).toFormat('h:mm a');
    const localEnd = DateTime.fromISO(`${slot.date}T${slot.endTime}`, { zone: effectiveTz }).setZone(userTimezone).toFormat('h:mm a');
    const localDate = DateTime.fromISO(`${slot.date}T${slot.startTime}`, { zone: effectiveTz }).setZone(userTimezone).toFormat('EEEE, MMMM d, yyyy');
    return {
      slotAbbr,
      displayNoTz,
      localInfo: { userAbbr, localDisplayNoTz: `${localDate} · ${localStart} – ${localEnd}` }
    };
  }, [slot.timezone, slot.date, slot.startTime, slot.endTime, userTimezone, displayText]);

  // ── UI state ──────────────────────────────────────────────────────────
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const autoExpandTriggered = useRef(false);
  const [showNewCommentModal, setShowNewCommentModal] = useState(false);
  const [newCommentDraft, setNewCommentDraft] = useState('');

  // Thread modal state
  const [threadRoot, setThreadRoot] = useState<MeetingComment | null>(null);
  const [threadReplyingTo, setThreadReplyingTo] = useState<string | null>(null);
  const [threadReplyDraft, setThreadReplyDraft] = useState('');
  const [threadEditingId, setThreadEditingId] = useState<string | null>(null);
  const [threadEditDraft, setThreadEditDraft] = useState('');

  // Inline list edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const { comments, commentCount, topLevelComments, isLoading, isSaving, isLoaded, loadCommentCount, loadComments, addComment, editComment } =
    useTimeSlotComments({
      meetingId,
      slotId: slot.slotId,
      creatorAlias,
      currentUserAlias,
      meetingSubject,
      slotLabel: formatTimeSlotLocalLabel(slot.date, slot.startTime, slot.endTime, slot.timezone)
    });

  // Load comment count eagerly so the 💬 pill shows the correct number
  React.useEffect(() => {
    loadCommentCount();
  }, [loadCommentCount]);

  // Auto-expand comments section when comments exist
  React.useEffect(() => {
    if (!autoExpandTriggered.current && commentCount > 0) {
      autoExpandTriggered.current = true;
      setCommentsExpanded(true);
      if (!isLoaded) loadComments();
    }
  }, [commentCount, isLoaded, loadComments]);

  // ── Helpers ───────────────────────────────────────────────────────────

  const getVoteForUser = (loginName: string): TimeSlotVote | undefined =>
    slot.votes.find((vote) => vote.loginName.toLowerCase() === loginName.toLowerCase());

  const sortByTime = (a: MeetingComment, b: MeetingComment) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

  const getDirectReplies = (parentId: string) => comments.filter((comment) => comment.parentCommentId === parentId).sort(sortByTime);

  const sortedTopLevel = [...topLevelComments].sort(sortByTime);

  // ── Inline list handlers ──────────────────────────────────────────────

  const handleToggleComments = async () => {
    if (!commentsExpanded && !isLoaded) await loadComments();
    setCommentsExpanded((prev) => !prev);
  };

  const handlePostNewComment = async () => {
    if (!newCommentDraft.trim()) return;
    await addComment(newCommentDraft.trim());
    setNewCommentDraft('');
    setShowNewCommentModal(false);
  };

  const handleVoteAndComment = async (slotId: string, vote: 'up' | 'down') => {
    onVote(slotId, vote);
  };

  // ── Thread modal handlers ─────────────────────────────────────────────

  const handleOpenThread = (rootComment: MeetingComment) => {
    setThreadRoot(rootComment);
    setThreadReplyingTo(null);
    setThreadReplyDraft('');
    setThreadEditingId(null);
    setThreadEditDraft('');
  };

  const handleCloseThread = () => {
    setThreadRoot(null);
    setThreadReplyingTo(null);
    setThreadReplyDraft('');
    setThreadEditingId(null);
    setThreadEditDraft('');
  };

  const handleThreadReply = (comment: MeetingComment) => {
    setThreadReplyingTo(comment.commentId);
    setThreadReplyDraft('');
  };

  const handleThreadPostReply = async (parentCommentId: string) => {
    if (!threadReplyDraft.trim()) return;
    await addComment(threadReplyDraft.trim(), parentCommentId);
    setThreadReplyDraft('');
    setThreadReplyingTo(null);
  };

  // Recursive renderer for replies inside thread modal
  const renderThreadReplies = (parentId: string): React.ReactNode => {
    const replies = getDirectReplies(parentId);
    if (replies.length === 0 && threadReplyingTo !== parentId) return null;

    return (
      <>
        {replies.map((reply) => (
          <CommentItem
            key={reply.commentId}
            comment={reply}
            currentUserAlias={currentUserAlias}
            commentVote={getVoteForUser(reply.loginName)}
            isEditing={threadEditingId === reply.commentId}
            editDraft={threadEditDraft}
            onEditDraftChange={setThreadEditDraft}
            onSaveEdit={async (id) => {
              if (!threadEditDraft.trim()) return;
              await editComment(id, threadEditDraft.trim());
              setThreadEditingId(null);
              setThreadEditDraft('');
            }}
            onCancelEdit={() => {
              setThreadEditingId(null);
              setThreadEditDraft('');
            }}
            onStartEdit={(id, content) => {
              setThreadEditingId(id);
              setThreadEditDraft(content);
            }}
            showReplyLink={true}
            onReply={handleThreadReply}
            variant="detailed"
          >
            {renderThreadReplies(reply.commentId)}
          </CommentItem>
        ))}

        {threadReplyingTo === parentId && (
          <div className="ts-inline-reply-row">
            <div className="ts-inline-reply-textarea">
              <Textarea
                value={threadReplyDraft}
                onChange={({ detail }) => setThreadReplyDraft(detail.value)}
                placeholder="Reply..."
                disabled={isSaving}
              />
            </div>
            <Button variant="primary" onClick={() => handleThreadPostReply(parentId)} disabled={!threadReplyDraft.trim() || isSaving}>
              Send
            </Button>
          </div>
        )}
      </>
    );
  };

  // ── Derived ───────────────────────────────────────────────────────────

  // Derive latest threadRoot from comments array so edits are reflected in the modal
  const currentThreadRoot = threadRoot ? comments.find((c) => c.commentId === threadRoot.commentId) ?? threadRoot : null;

  const upCommentClass = `vote-comment${currentUserVote?.vote === 'up' ? ' vote-comment--active' : ''}`;
  const downCommentClass = `vote-comment${currentUserVote?.vote === 'down' ? ' vote-comment--active' : ''}`;

  return (
    <div className="time-slot-card">
      {/* Slot row */}
      <div className="time-slot-card__row">
        <div className="time-slot-card__tz-group">
          <div className="time-slot-card__tz-item">
            <Box fontSize="body-s" fontWeight="bold">
              <span className="tz-badge">{tzDisplay.slotAbbr}</span> {tzDisplay.displayNoTz}
            </Box>
          </div>
          {tzDisplay.localInfo && (
            <>
              <span className="tz-divider" />
              <div className="time-slot-card__tz-item">
                <Box fontSize="body-s" fontWeight="bold">
                  <span className="tz-badge tz-badge--local">{tzDisplay.localInfo.userAbbr}</span> {tzDisplay.localInfo.localDisplayNoTz}
                </Box>
              </div>
            </>
          )}
          {isLocked && <span className="time-slot-card__locked-label">🔒 Locked in Meeting Time</span>}
        </div>

        <div className="time-slot-card__spacer" />

        <div className="time-slot-card__right">
          <div className="time-slot-card__pills">
            <button className={upCommentClass} aria-label="Vote yes" onClick={() => handleVoteAndComment(slot.slotId, 'up')}>
              <span className="vote-comment-emoji">👍</span>
              {upVoters.length}
            </button>
            <button className={downCommentClass} aria-label="Vote no" onClick={() => handleVoteAndComment(slot.slotId, 'down')}>
              <span className="vote-comment-emoji">👎</span>
              {downVoters.length}
            </button>
            <button className="vote-comment" aria-label="Add comment" onClick={() => setShowNewCommentModal(true)}>
              <span className="vote-comment-emoji">💬</span>
              {commentCount}
            </button>
          </div>
        </div>
      </div>

      {/* View Comments toggle */}
      {!commentsExpanded && (
        <div className="ts-comment-toggle-row">
          <button className="ts-comment-toggle-btn" onClick={handleToggleComments}>
            <Icon name="angle-down" size="small" /> View Comments
          </button>
        </div>
      )}

      {/* Inline top-level comments (compact single-line) */}
      {commentsExpanded && (
        <div className="ts-comments-section">
          {isLoading ? (
            <Box textAlign="center" padding="s">
              <Spinner size="normal" />
            </Box>
          ) : sortedTopLevel.length === 0 ? (
            <Box fontSize="body-s" color="text-body-secondary" padding={{ vertical: 'xs' }}>
              No comments yet.
            </Box>
          ) : (
            sortedTopLevel.map((topComment) => {
              const replyCount = getDirectReplies(topComment.commentId).length;
              return (
                <CommentItem
                  key={topComment.commentId}
                  comment={topComment}
                  currentUserAlias={currentUserAlias}
                  commentVote={getVoteForUser(topComment.loginName)}
                  isEditing={editingId === topComment.commentId}
                  editDisabled={editingId !== null && editingId !== topComment.commentId}
                  editDraft={editDraft}
                  onEditDraftChange={setEditDraft}
                  onSaveEdit={async (id) => {
                    if (!editDraft.trim()) return;
                    await editComment(id, editDraft.trim());
                    setEditingId(null);
                    setEditDraft('');
                  }}
                  onCancelEdit={() => {
                    setEditingId(null);
                    setEditDraft('');
                  }}
                  onStartEdit={(id, content) => {
                    setEditingId(id);
                    setEditDraft(content);
                  }}
                  showThreadLink={true}
                  onOpenThread={handleOpenThread}
                  replyCount={replyCount}
                  variant="inline"
                />
              );
            })
          )}

          <div className="ts-comment-toggle-row">
            <button className="ts-comment-toggle-btn" onClick={handleToggleComments}>
              <Icon name="angle-up" size="small" /> Hide Comments
            </button>
          </div>
        </div>
      )}

      {/* Add Comment modal */}
      {showNewCommentModal && (
        <Modal
          visible={true}
          onDismiss={() => {
            setShowNewCommentModal(false);
            setNewCommentDraft('');
          }}
          header="Add Comment"
          size="medium"
          footer={
            <Box float="right">
              <span className="blue-button">
                <Button variant="primary" onClick={handlePostNewComment} disabled={!newCommentDraft.trim() || isSaving} loading={isSaving}>
                  Send
                </Button>
              </span>
            </Box>
          }
        >
          <FormField label="">
            <Textarea
              value={newCommentDraft}
              onChange={({ detail }) => setNewCommentDraft(detail.value)}
              placeholder="Write your comment here..."
              disabled={isSaving}
            />
          </FormField>
        </Modal>
      )}

      {/* Thread modal — parent comment + nested replies (detailed layout) */}
      {currentThreadRoot && (
        <Modal visible={true} onDismiss={handleCloseThread} header={`Thread — ${currentThreadRoot.loginName}`} size="medium">
          <div className="ts-thread-modal-body">
            <CommentItem
              comment={currentThreadRoot}
              currentUserAlias={currentUserAlias}
              commentVote={getVoteForUser(currentThreadRoot.loginName)}
              isEditing={threadEditingId === currentThreadRoot.commentId}
              editDraft={threadEditDraft}
              onEditDraftChange={setThreadEditDraft}
              onSaveEdit={async (id) => {
                if (!threadEditDraft.trim()) return;
                await editComment(id, threadEditDraft.trim());
                setThreadEditingId(null);
                setThreadEditDraft('');
              }}
              onCancelEdit={() => {
                setThreadEditingId(null);
                setThreadEditDraft('');
              }}
              onStartEdit={(id, content) => {
                setThreadEditingId(id);
                setThreadEditDraft(content);
              }}
              showReplyLink={true}
              onReply={handleThreadReply}
              variant="detailed"
            >
              {renderThreadReplies(currentThreadRoot.commentId)}
            </CommentItem>
          </div>
        </Modal>
      )}
    </div>
  );
};
